import React from "react";
import PropTypes from "prop-types";
import Select from "react-select";

import queryString from "query-string";

import getAssetAttachment from '@api/v4/assets/attachments';

import ClipboardCopy from "@components/common/clipboard_copy/main";
import AttachmentSidebar from '../shared/attachment_sidebar/main';
import VisibleAttachment from "../shared/visible_attachment/main";

import "./styles/main.scss";

class AssetEmbed extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      attachments: [],
      visibleAttachment: null,
      selectedWidth: "",
      selectedHeight: "",
      selectedFormat: {
        value: "",
        label: "",
      },
      loading: true,
    };

    this.activeRequests = [];
  }

  /* NOTE :: When you have a parent modal component that grabs the asset info
  you won't need to grab the attachments on each tab, you can grab them when
  you load the modal and then just pass them down. */
  componentDidMount() {
    let params = {
      assetKey: this.props.assetKey,
      fields:
        "best_metadata,other_metadata,download_url,thumbnail_url,view_thumbnail_retina,extension,thumbnailed,conversion_options,cdn_url",
    };

    params = BFG.manifestDigest ? { ...params, digest: BFG.manifestDigest } : params;
    params = this.props.watermarkResourceKey
      ? { ...params, watermark_resource_key: this.props.watermarkResourceKey }
      : params;

    getAssetAttachment(
      {
        assetKey: this.props.assetKey,
        params,
      },
      this.updateFetchControllers
    )
      .then((response) => {
        const sortedAttachments = response.data?.sort((a, b) => (a.attributes.position > b.attributes.position ? 1 : -1));
        const firstAttachment = sortedAttachments?.[0];

        // passing in a different extension if we get it from cdn_url
        const cdnFormat = queryString.parse(firstAttachment?.attributes?.cdn_url).format;
        this.setState({
          attachments: sortedAttachments,
          loading: false,
          visibleAttachment: firstAttachment,
          selectedWidth: firstAttachment?.attributes?.width || 0,
          selectedHeight: firstAttachment?.attributes?.height || 0,
          selectedFormat: this.formatSelectedFormat(firstAttachment, cdnFormat),
        });
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  }

  componentWillUnmount() {
    this.abortFetchControllers();
  }

  get iframe() {
    if (!this.state.visibleAttachment) {
      return null;
    }

    return `<iframe src='https://brandfolder.com/${this.props.brandfolderSlug}/attachments/embed/${this.state.visibleAttachment.id}?resource_type=Brandfolder&frame=auto&animated=false&loop=false&muted=false&autoplay=false' frameborder='0' allowfullscreen></iframe>`;
  }

  get formatOptions() {
    if (!this.state.visibleAttachment) {
      return null;
    }

    const formatOptions = [
      { value: "auto", label: "AUTO" },
      { value: "jpg", label: "JPG" },
      { value: "png", label: "PNG" },
      { value: "pdf", label: "PDF" },
      { value: "gif", label: "GIF" },
    ];
    const visibleExtension = this.state.visibleAttachment.attributes.extension;

    if (!formatOptions.find((option) => option.value === visibleExtension)) {
      formatOptions.push({
        value: visibleExtension,
        label: visibleExtension.toUpperCase(),
      });
    }
    return formatOptions;
  }

  updateFetchControllers = (fetchController) => {
    this.activeRequests = [...this.activeRequests, fetchController];
  };

  abortFetchControllers = () => {
    this.activeRequests.forEach((fetchController) => {
      if (!fetchController.signal.aborted) {
        fetchController.abort();
      }
    });
  };

  formatSelectedFormat = (attachment, passedFormat = null) => {
    const fileFormat = passedFormat || attachment?.attributes?.extension || "";
    return { value: fileFormat.toLowerCase(), label: fileFormat.toUpperCase() };
  };

  stripExtensionFromName = (attachmentExtension, attachmentName) => {
    const extensionFromName = attachmentName.split(".").pop();
    // strip file extension from filename
    if (
      attachmentExtension
      && attachmentExtension.toLowerCase() === extensionFromName.toLowerCase()
    ) {
      const attachmentNameUpdated = attachmentName.split(".");
      attachmentNameUpdated.pop();
      return attachmentNameUpdated.join(".");
    }
    return attachmentName;
  };

  getV1AssetLink = (url) => {
    // url is good, just need to update extension
    // v1 = "http://fastly_v1.lvh.me:3000/6IGOKVHG/as/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2.png"
    const {
      selectedFormat: { value },
      selectedHeight,
      selectedWidth,
      visibleAttachment: {
        attributes: { height, position, width },
      },
    } = this.state;
    const pos = url.includes(".") ? url.lastIndexOf(".") : url.length;
    const fileRoot = url.substr(0, pos);
    // overwriting the png default extension with the state value
    // EMMI: This would be the place to fix when you have multiple format pickers
    const output = `${fileRoot}.${value}`;

    const assetUrl = new URL(output);
    const parsed = queryString.parse(assetUrl.search);

    // not mucking with format here, just changing extension

    if (width && selectedWidth !== width && selectedWidth !== "") {
      parsed.width = selectedWidth;
    }

    if (height && selectedHeight !== height && selectedHeight !== "") {
      parsed.height = selectedHeight;
    }

    if (position > 0) {
      parsed.position = position + 1;
    }

    assetUrl.search = queryString.stringify(parsed);
    const assetLink = assetUrl.toString();
    return assetLink;
  };

  getV1AttachmentLink = (url) => {
    // url is good, just need to update extension
    // v1 = "http://fastly_v1.lvh.me:3000/6IGOKVHG/at/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2.jpg"
    const {
      selectedFormat: { value },
      selectedHeight,
      selectedWidth,
      visibleAttachment: {
        attributes: { height, width },
      },
    } = this.state;
    const pos = url.includes(".") ? url.lastIndexOf(".") : url.length;
    const fileRoot = url.substr(0, pos);
    const output = `${fileRoot}.${value}`;

    const attachmentUrl = new URL(output);
    const parsed = queryString.parse(attachmentUrl.search);

    // not mucking with format here, just changing extension

    if (width && selectedWidth !== width && selectedWidth !== "") {
      parsed.width = selectedWidth;
    }

    if (height && selectedHeight !== height && selectedHeight !== "") {
      parsed.height = selectedHeight;
    }

    attachmentUrl.search = queryString.stringify(parsed);
    const attachmentLink = attachmentUrl.toString();

    return attachmentLink;
  };

  getV2AssetLink = (url) => {
    // v2 = "http://fastly_v2.lvh.me:3000/6IGOKVHG/as/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2"
    const {
      selectedFormat: { value },
      selectedHeight,
      selectedWidth,
      visibleAttachment: {
        attributes: { height, position, width, extension },
      },
    } = this.state;

    const assetUrl = new URL(url);
    const parsed = queryString.parse(assetUrl.search);

    if (value !== extension) {
      parsed.format = value;
    }

    if (width && selectedWidth !== width && selectedWidth !== "") {
      parsed.width = selectedWidth;
    }

    if (height && selectedHeight !== height && selectedHeight !== "") {
      parsed.height = selectedHeight;
    }

    if (position > 0) {
      parsed.position = position + 1;
    }

    assetUrl.search = queryString.stringify(parsed);
    const assetLink = assetUrl.toString();
    return assetLink;
  };

  getV2AttachmentLink = (url) => {
    // v2 = "http://fastly_v1.lvh.me:3000/6IGOKVHG/at/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2.jpg"
    const {
      selectedFormat: { value },
      selectedHeight,
      selectedWidth,
      visibleAttachment: {
        attributes: { height, extension, width },
      },
    } = this.state;

    const attachmentUrl = new URL(url);
    const parsed = queryString.parse(attachmentUrl.search);

    if (value !== extension) {
      parsed.format = value;
    }

    if (width && selectedWidth !== width && selectedWidth !== "") {
      parsed.width = selectedWidth;
    }

    if (height && selectedHeight !== height && selectedHeight !== "") {
      parsed.height = selectedHeight;
    }

    attachmentUrl.search = queryString.stringify(parsed);
    const attachmentLink = attachmentUrl.toString();
    return attachmentLink;
  };

  getV21AssetLink = (url) => {
    // v2_1 = "http://fastly_v2.lvh.me:3000/6IGOKVHG/as/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2?format=png&webp=auto"
    const {
      selectedFormat: { value },
      selectedHeight,
      selectedWidth,
      visibleAttachment: {
        attributes: { height, position, width },
      },
    } = this.state;

    const assetUrl = new URL(url);
    const parsed = queryString.parse(assetUrl.search);

    // This is trying to append format=avi to the cdn urls, which cause fastly to fail.
    if (parsed.format) {
      parsed.format = value;
    }

    if (width && selectedWidth !== width && selectedWidth !== "") {
      parsed.width = selectedWidth;
    }

    if (height && selectedHeight !== height && selectedHeight !== "") {
      parsed.height = selectedHeight;
    }

    if (position > 0) {
      parsed.position = position + 1;
    }

    assetUrl.search = queryString.stringify(parsed);
    const assetLink = assetUrl.toString();
    return assetLink;
  };

  getV21AttachmentLink = (url) => {
    // url is good, just need to update extension
    // v21 = "http://fastly_v2.lvh.me:3000/6IGOKVHG/at/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2.jpg?format=png&webp=auto"
    const {
      selectedFormat: { value },
      selectedHeight,
      selectedWidth,
      visibleAttachment: {
        attributes: { height, width },
      },
    } = this.state;

    const attachmentUrl = new URL(url);
    const parsed = queryString.parse(attachmentUrl.search);

    parsed.format = value;

    if (width && selectedWidth !== width && selectedWidth !== "") {
      parsed.width = selectedWidth;
    }

    if (height && selectedHeight !== height && selectedHeight !== "") {
      parsed.height = selectedHeight;
    }

    attachmentUrl.search = queryString.stringify(parsed);
    const attachmentLink = attachmentUrl.toString();
    return attachmentLink;
  };

  getV21OriginalLink = (url) => url.split("?")[0];

  link = () => {
    if (!this.state.visibleAttachment) {
      return { assetLink: null, attachmentLink: null };
    }

    // assets
    // # v1   = "http://fastly_v1.lvh.me:3000/6IGOKVHG/as/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2.png"
    // # v2   = "http://fastly_v2.lvh.me:3000/6IGOKVHG/as/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2"
    // # v2_1 = "http://fastly_v2.lvh.me:3000/6IGOKVHG/as/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2?format=png&webp=auto"

    // attachments
    // # v1   = "http://fastly_v1.lvh.me:3000/6IGOKVHG/at/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2.jpg"
    // # v2   = "http://fastly_v2.lvh.me:3000/6IGOKVHG/at/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2"
    // # v2_1 = "http://fastly_v2.lvh.me:3000/6IGOKVHG/at/qc1a16-g67t8o-t9ye/3784-bliffert_DSC_0229_3_2.jpg?format=png&webp=auto"
    const { assetCdnUrl } = this.props;
    const {
      visibleAttachment: { attributes: { cdn_url } },
    } = this.state;

    switch (BFG.smartCDNSource) {
      case "fastly":
        return {
          assetLink: this.getV1AssetLink(assetCdnUrl),
          attachmentLink: this.getV1AttachmentLink(cdn_url),
        };
      case "fastly_v2":
        // NOTE that for v2 we don't use extensions, they're confusing with their intent. format=<> conveys desire
        return {
          assetLink: this.getV2AssetLink(assetCdnUrl),
          attachmentLink: this.getV2AttachmentLink(cdn_url),
        };
      case "fastly_v2_1":
        return {
          assetLink: this.getV21AssetLink(assetCdnUrl),
          attachmentLink: this.getV21AttachmentLink(cdn_url),
          originalLink: this.getV21OriginalLink(cdn_url),
        };
      default:
        return {
          assetLink: this.getV1AssetLink(assetCdnUrl),
          attachmentLink: this.getV1AttachmentLink(cdn_url),
        };
    }
  };

  maintainImageSizeRatio = (e) => {
    let { height, width } = this.state.visibleAttachment.attributes;
    const updatedValue = parseInt(e.target.value, 10);
    const aspectRatio = width / height;
    const isWidth = e.target.className && e.target.className.indexOf("asset-width") > -1;

    if (!Number.isInteger(updatedValue)) {
      width = "";
      height = "";
    } else if (isWidth) {
      width = updatedValue;
      height = Math.round(width / aspectRatio);
    } else {
      height = updatedValue;
      width = Math.round(height * aspectRatio);
    }

    this.setState({ selectedWidth: width, selectedHeight: height });
  };

  updateActiveImage = ({ id: attachmentId }) => {
    if (attachmentId !== this.state.visibleAttachment.id) {
      this.setState((prevState) => {
        const visible = prevState.attachments.find((attachment) => attachment.id === attachmentId);
        return {
          visibleAttachment: visible,
          selectedFormat: this.formatSelectedFormat(visible),
          selectedWidth: visible?.attributes?.width || 0,
          selectedHeight: visible?.attributes?.height || 0,
        };
      });
    }
  };

  renderEmbedLink(link) {
    if (link === undefined) return <div />;
    return (
      <pre className="embed-link">
        <code>
          <span className="embed-link">{link}</span>
        </code>
      </pre>
    );
  }

  renderOriginalLinkCopy(originalLink) {
    if (originalLink === undefined) return <div />;
    return (
      <div className="copy-link-container">
        <div className="cdn-title">
          <span className="bf-label bf-label--primary">
            {bfTranslate("Original File CDN Link")}
          </span>
        </div>
        <ClipboardCopy check showFeedbackAsTooltip={false} textToCopy={originalLink}>
          <span className="bff-copy" />
          {bfTranslate("Copy link")}
        </ClipboardCopy>
      </div>
    );
  }

  render() {
    const { attachments, loading, visibleAttachment } = this.state;
    const isVideo = visibleAttachment?.attributes?.mimetype
      && visibleAttachment.attributes.mimetype.indexOf("video") > -1;
    const activeCard = visibleAttachment ? { id: visibleAttachment.id, type: "attachment" } : null;

    const { assetLink, attachmentLink, originalLink } = this.link();

    return (
      <div className="flex-wrapper asset-embed-component">
        <AttachmentSidebar
          activeCard={activeCard}
          attachments={attachments.length ? attachments : null}
          updateActiveCard={this.updateActiveImage}
        />
        {loading ? (
          <div className="flex-two-thirds no-similar-details loader-container">
            <video
              className="brandfolder-loader"
              height="90"
              poster="https://cdn.brandfolder.io/4OQZ5PW1/as/pxmju5-qfr48-21nt37/generic-loader.gif"
              width="90"
            />
          </div>
        ) : (
          <div className="modal-asset-two-thirds flex-two-thirds embed-details bf-scroll-element">
            {!BFG.hasFeature("embed_asset") && (
              <div className="embed-details__no-support">
                <div className="embed-details__no-support--details flex-two-thirds">
                  <h3>{bfTranslate("Your account doesn’t currently support embedding.")}</h3>
                  <p>
                    {bfTranslate(
                      "Embedding with CDN (Content Delivery Network) Links allow you to embed assets on external websites, while housing the single source of truth version in"
                    )}{" "}
                    {this.props.libraryName}.
                  </p>
                  <p>
                    {bfTranslate(
                      "Do you have special requirements? Your account manager can answer questions and get you a quote for the embed feature based on your specific needs."
                    )}
                  </p>
                  <a href={`mailto:${this.props.supportEmail}`}>
                    <button className="button primary" type="button">
                      {bfTranslate("Get a Quote")}
                    </button>
                  </a>
                </div>
                <div className="embed-details__no-support--list flex-one-third">
                  <p>{bfTranslate("With CDN embedding, you can:")}</p>
                  <ul>
                    <li>
                      <span className="bff-confirmation" />
                      {bfTranslate("Edit or replace assets in")} {this.props.libraryName}{" "}
                      {"and see the changes reflected on external sites"}
                    </li>
                    <li>
                      <span className="bff-confirmation" />
                      {bfTranslate("Track asset usage outside of")} {this.props.libraryName}
                    </li>
                    <li>
                      <span className="bff-confirmation" />
                      {bfTranslate(
                        "Ensure assets that live in marketing campaigns and digital channels are never out of date"
                      )}
                    </li>
                  </ul>
                </div>
              </div>
            )}
            <div className={!BFG.hasFeature("embed_asset") ? "disabled" : ""}>
              {visibleAttachment && visibleAttachment.attributes && (
                <>
                  <VisibleAttachment
                    extension={visibleAttachment.attributes.extension}
                    filename={visibleAttachment.attributes.filename}
                    height={visibleAttachment.attributes.height}
                    size={visibleAttachment.attributes.size}
                    thumbnail={visibleAttachment.attributes.thumbnail_url}
                    width={visibleAttachment.attributes.width}
                  />
                  <div className="embed-text">
                    <h3>{bfTranslate("Embed this asset")}</h3>
                    <p>
                      {bfTranslate(
                        "By embedding this link you are acknowledging that it is accessible to anyone from where it is embedded."
                      )}
                    </p>
                  </div>
                  <hr />
                  {isVideo ? (
                    <div className="cdn-builder">
                      <div className="copy-link-container">
                        <span className="bf-label bf-label--primary">
                          {bfTranslate("Video Embed Code ")}
                        </span>
                        <ClipboardCopy check showFeedbackAsTooltip={false} textToCopy={this.iframe}>
                          <span className="bff-copy" />
                          {bfTranslate(" Copy to clipboard")}
                        </ClipboardCopy>
                      </div>
                      <pre className="embed-link">
                        <code>
                          <span className="embed-link">{this.iframe}</span>
                        </code>
                      </pre>
                      <div className="copy-link-container">
                        <span className="bf-label bf-label--primary">
                          {bfTranslate("Asset CDN Link")}
                        </span>
                        <ClipboardCopy check showFeedbackAsTooltip={false} textToCopy={assetLink}>
                          <span className="bff-copy" />
                          {bfTranslate("Copy link")}
                        </ClipboardCopy>
                      </div>
                      <pre className="embed-link">
                        <code>
                          <span className="embed-link">{assetLink}</span>
                        </code>
                      </pre>
                      <p>
                        {bfTranslate("See our ")}
                        <a
                          href="https://help.brandfolder.com/hc/en-us/articles/360005174134-Embedding-Assets-with-Smart-CDN"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {bfTranslate("CDN documentation")}
                        </a>
                      </p>
                    </div>
                  ) : (
                    <div className="cdn-builder">
                      <div className="copy-link-container">
                        <div className="cdn-title">
                          <span className="bf-label bf-label--primary">
                            {bfTranslate("Asset CDN Link")}
                          </span>
                        </div>
                        <ClipboardCopy check showFeedbackAsTooltip={false} textToCopy={assetLink}>
                          <span className="bff-copy" />
                          {bfTranslate("Copy link")}
                        </ClipboardCopy>
                      </div>
                      <pre className="embed-link">
                        <code>
                          <span className="embed-link">{assetLink}</span>
                        </code>
                      </pre>
                      <div className="copy-link-container">
                        <div className="cdn-title">
                          <span className="bf-label bf-label--primary">
                            {bfTranslate("Attachment CDN Link")}
                          </span>
                        </div>
                        <ClipboardCopy
                          check
                          showFeedbackAsTooltip={false}
                          textToCopy={attachmentLink}
                        >
                          <span className="bff-copy" />
                          {bfTranslate("Copy link")}
                        </ClipboardCopy>
                      </div>
                      <pre className="embed-link">
                        <code>
                          <span className="embed-link">{attachmentLink}</span>
                        </code>
                      </pre>
                      { this.renderOriginalLinkCopy(originalLink) }
                      { this.renderEmbedLink(originalLink) }
                      <div className="embed-input">
                        <div className="format-select">
                          <label className="brandfolder-label">{bfTranslate("Format")}</label>
                          <Select
                            clearable={false}
                            onChange={(selected) => this.setState({ selectedFormat: selected })}
                            options={this.formatOptions}
                            searchable={false}
                            value={this.state.selectedFormat}
                          />
                        </div>
                        <div className="resize-dimension-container">
                          <div className="width-input">
                            <label className="brandfolder-label">{bfTranslate("Width (px)")}</label>
                            <input
                              className="asset-width brandfolder-input"
                              onChange={this.maintainImageSizeRatio}
                              value={this.state.selectedWidth}
                            />
                          </div>
                          <span className="bff-private" />
                          <div className="height-input">
                            <label className="brandfolder-label">
                              {bfTranslate("Height (px)")}
                            </label>
                            <input
                              className="asset-height brandfolder-input"
                              onChange={this.maintainImageSizeRatio}
                              value={this.state.selectedHeight}
                            />
                          </div>
                        </div>
                      </div>
                      <p>
                        {bfTranslate("See our ")}
                        <a
                          href="https://help.brandfolder.com/hc/en-us/articles/360005174134-Embedding-Assets-with-Smart-CDN"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {bfTranslate("CDN documentation")}
                        </a>
                      </p>
                      <p>
                        <strong>{bfTranslate("AUTO")}</strong>{" "}
                        {bfTranslate(
                          "Automatically chooses the best format (WEBP or PNG) to optimize the load time of images based on the users browser."
                        )}
                      </p>
                    </div>
                  )}
                  <hr />
                  <div className="api-link">
                    <div className="copy-link-container">
                      <h3>{bfTranslate("API Usage")}</h3>
                    </div>
                    <p>
                      {bfTranslate("See our ")}
                      <a
                        href="https://developers.brandfolder.com"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {bfTranslate("API documentation")}
                      </a>
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

AssetEmbed.propTypes = {
  assetKey: PropTypes.string.isRequired,
  brandfolderSlug: PropTypes.string.isRequired,
  libraryName: PropTypes.string.isRequired,
  supportEmail: PropTypes.string.isRequired,
  watermarkResourceKey: PropTypes.string,
  assetCdnUrl: PropTypes.string.isRequired,
};

AssetEmbed.defaultProps = {
  watermarkResourceKey: undefined,
};

export default AssetEmbed;
